import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import {
  MiBox,
  MiButton,
  MiControlledSelectBox,
  MiStackedTypography,
} from "@miplatform/components";
import { ISessionData } from "@miplatform/types";
import { Box, Typography } from "@mui/material";
import { usePermissions, useSession, useUsers } from "../../hooks";
import { useSubscriptions } from "../../hooks/useSubscriptions/useSubscriptions";
import { useUserStore } from "../../stores";

export const LocalUserControls = () => {
  const { classes } = useStyles();

  const { permissions } = usePermissions();
  const { session, setSession } = useSession();
  const getProfile = useUserStore((state) => state.getProfile);

  const { getUsers, users, setUsers } = useUsers();
  const { getSubscriptions, subscriptions, setSubscriptions } =
    useSubscriptions();

  const {
    control,
    formState: { isDirty },
    getValues,
    reset,
  } = useForm({
    mode: "onChange",
    defaultValues: {
      userId: session?.userId || "",
      tenantId: session?.tenantId || "",
    },
  });

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const fetchedUsers = await getUsers();
        if (fetchedUsers && Array.isArray(fetchedUsers.data)) {
          setUsers(fetchedUsers.data);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to fetch users", error);
      }
    };
    fetchUsers();
  }, [session?.userId, session?.tenantId]);

  const userOptions = users?.map((user) => ({
    label: `${user.displayName}`,
    value: user.id,
  }));

  useEffect(() => {
    const fetchSubscriptions = async () => {
      try {
        const fetchedSubscriptions = await getSubscriptions();
        if (fetchedSubscriptions && Array.isArray(fetchedSubscriptions.data)) {
          setSubscriptions(fetchedSubscriptions.data);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to fetch users", error);
      }
    };
    fetchSubscriptions();
  }, [session?.tenantId]);

  const tenantOptions = subscriptions?.map((sub) => ({
    label: sub.name,
    value: sub.id,
    domain: sub.domain,
  }));

  const handleChangeUser = () => {
    const userId = getValues("userId");
    if (userId && session) {
      setSession({
        ...session,
        userId,
      } as ISessionData);
      getProfile();
    }
    reset({ userId }, { keepDirty: false });
  };

  const handleChangeTenant = () => {
    const tenantId = getValues("tenantId");
    if (tenantId && session) {
      const sub = tenantOptions.find((x) => x.value === tenantId)!;
      setSession({
        ...session,
        tenantId,
        domain: sub.domain,
      } as ISessionData);
      getProfile();
    }
    reset({ tenantId }, { keepDirty: false });
    window.location.reload();
  };

  const currentUser = users?.find((user) => user.id === session?.userId);

  return (
    <MiBox styles={classes.localUserControlsContainer}>
      <Typography variant="HD4">Local User Controls </Typography>
      <Box display="flex" flexDirection="column" mt={2} gap={2}>
        <Typography variant="HD3">Select a user to impersonate:</Typography>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}>
          <MiControlledSelectBox
            name="userId"
            label="Select User"
            formControl={control}
            options={userOptions}
            disableNone
          />
          <MiButton
            title="Change User"
            onClick={handleChangeUser}
            disabled={!isDirty}
            variant="contained"
          />
        </Box>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}>
          <MiControlledSelectBox
            name="tenantId"
            label="Select tenant"
            formControl={control}
            options={tenantOptions}
            disableNone
          />
          <MiButton
            title="Change Tenant"
            onClick={handleChangeTenant}
            disabled={!isDirty}
            variant="contained"
          />
        </Box>
      </Box>
      <Box>
        <Typography variant="HD3">Current User Info:</Typography>
        <Box display="flex" gap={4} mt={2}>
          <Box flex={1}>
            <MiStackedTypography
              title="Name"
              description={
                currentUser?.firstName && currentUser?.lastName
                  ? `${currentUser?.firstName} ${currentUser?.lastName}`
                  : "N/A"
              }
            />
            <MiStackedTypography
              title="Display Name"
              description={currentUser?.displayName || "N/A"}
            />
            <MiStackedTypography
              title="Roles"
              description={
                currentUser?.roles?.map((role) => role.name).join(", ") || "N/A"
              }
              descriptionProps={{ sx: { textWrap: "wrap" } }}
            />
            <MiStackedTypography
              title="User ID"
              description={session?.userId || "N/A"}
            />
            <MiStackedTypography
              title="Tenant ID"
              description={session?.tenantId || "N/A"}
            />
          </Box>
          <Box flex={1}>
            <pre>{JSON.stringify(Array.from(permissions), null, 2)}</pre>
          </Box>
        </Box>
      </Box>
    </MiBox>
  );
};

const useStyles = makeStyles()((theme) => ({
  localUserControlsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    width: "100%",
    height: "inherit",
  },
}));
